import React, { useState } from 'react';
import {
  Box, CardContent, Typography, Avatar, Chip, Divider, Tooltip, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
  SportsSoccer as BallIcon,
  EmojiEvents as TrophyIcon,
  GpsFixed as PenIcon,
} from '@mui/icons-material';
import { C, POSITION_LABELS } from './constants';
import { GlowCard } from './ui';
import { initials } from './utils';

/* ─── Position colour ────────────────────────────────────────────────────── */
const posColor = (pos) => {
  if (!pos) return C.muted;
  if (pos === 'GK')                                    return '#A78BFA';
  if (['CB','RB','LB','RWB','LWB','SW'].includes(pos)) return C.emerald;
  if (['CDM','CM','CAM','RM','LM'].includes(pos))      return C.accent;
  return C.rose;
};

/* ─── Coloured card rectangle ────────────────────────────────────────────── */
const CardRect = ({ color }) => (
  <Box sx={{ width: 8, height: 11, borderRadius: '1.5px', background: color, flexShrink: 0 }} />
);

/* ─── Single stat pill ───────────────────────────────────────────────────── */
const Pill = ({ value, color, label, icon }) => (
  <Tooltip title={label} arrow placement="top">
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, minWidth: 38, justifyContent: 'flex-end' }}>
      {icon}
      <Typography sx={{
        fontFamily: 'Syne', fontWeight: 700, color,
        fontSize: '0.88rem', lineHeight: 1,
        opacity: value === 0 ? 0.25 : 1,
      }}>
        {value}
      </Typography>
    </Box>
  </Tooltip>
);

/* ─── Column header ──────────────────────────────────────────────────────── */
const ColH = ({ label, minWidth = 38, align = 'right' }) => (
  <Typography sx={{
    fontFamily: 'DM Sans', color: C.dim, fontSize: '0.62rem',
    textTransform: 'uppercase', letterSpacing: '0.06em',
    minWidth, textAlign: align,
  }}>
    {label}
  </Typography>
);

/* ─── Views ──────────────────────────────────────────────────────────────── */
const VIEWS = [
  { value: 'goals',     label: 'Buts'     },
  { value: 'cards',     label: 'Cartons'  },
  { value: 'penalties', label: 'Pénaltys' },
];

const sortByView = (a, b, view) => {
  if (view === 'goals')     return (b.goals - a.goals) || (b.assists - a.assists);
  if (view === 'cards')     return (b.yellowCards + b.redCards * 2) - (a.yellowCards + a.redCards * 2);
  if (view === 'penalties') return (b.penaltiesScored + b.penaltiesMissed) - (a.penaltiesScored + a.penaltiesMissed);
  return 0;
};

const hasActivity = (s, view) => {
  if (view === 'goals')     return s.goals > 0 || s.assists > 0;
  if (view === 'cards')     return s.yellowCards > 0 || s.redCards > 0;
  if (view === 'penalties') return s.penaltiesScored > 0 || s.penaltiesMissed > 0;
  return false;
};

/* ─── Player row ─────────────────────────────────────────────────────────── */
const PlayerRow = ({ player, stats, rank, index, view }) => {
  const fullName = [player.firstName, player.lastName].filter(Boolean).join(' ') || player.email || 'Inconnu';
  const pos    = player.teamMemberInfo?.positionOnField;
  const color  = posColor(pos);
  const jersey = player.teamMemberInfo?.jerseyNumber;

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1.2, py: 1.1,
      borderBottom: `1px solid ${C.border}`,
      animation: `fadeUp 0.4s ease both`,
      animationDelay: `${index * 50 + 300}ms`,
      '&:last-child': { borderBottom: 'none' },
      transition: 'background 0.2s', borderRadius: '8px', px: 1,
      '&:hover': { background: `${C.border}50` },
    }}>

      {/* Rank */}
      <Typography sx={{
        fontFamily: 'Syne', fontWeight: 800,
        color: rank <= 3 ? C.gold : C.dim,
        fontSize: '0.75rem', minWidth: 16, textAlign: 'center',
      }}>
        {rank}
      </Typography>

      {/* Avatar */}
      <Avatar sx={{
        width: 32, height: 32,
        background: `linear-gradient(135deg, ${color}40, ${color}20)`,
        border: `1px solid ${color}50`,
        color, fontFamily: 'Syne', fontWeight: 700, fontSize: '0.68rem',
      }}>
        {initials(fullName)}
      </Avatar>

      {/* Name + position chip + games played */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
          <Typography noWrap sx={{ fontFamily: 'DM Sans', fontWeight: 500, color: C.text, fontSize: '0.82rem' }}>
            {fullName}
          </Typography>
          {jersey && (
            <Typography sx={{ color: C.dim, fontSize: '0.7rem', flexShrink: 0 }}>#{jersey}</Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.15 }}>
          {pos && (
            <Tooltip title={POSITION_LABELS[pos] || pos} arrow placement="right">
              <Chip label={pos} size="small" sx={{
                height: 14,
                background: `${color}18`, border: `1px solid ${color}35`, color,
                fontFamily: 'Syne', fontWeight: 700, fontSize: '0.56rem', cursor: 'default',
                '& .MuiChip-label': { px: 0.7 },
              }} />
            </Tooltip>
          )}
          <Typography sx={{ color: C.dim, fontSize: '0.65rem', fontFamily: 'DM Sans' }}>
            {stats.gamesPlayed}J
          </Typography>
        </Box>
      </Box>

      {/* ── Stats by view ── */}
      {view === 'goals' && (
        <>
          <Pill
            value={stats.goals} color={C.emerald} label="Buts marqués"
            icon={<BallIcon sx={{ fontSize: 12, color: C.emerald }} />}
          />
          <Pill
            value={stats.assists} color={C.accent} label="Passes décisives"
            icon={<Typography sx={{ fontFamily:'Syne', fontWeight:700, color:C.accent, fontSize:'0.6rem', lineHeight:1 }}>A</Typography>}
          />
        </>
      )}

      {view === 'cards' && (
        <>
          <Pill value={stats.yellowCards} color={C.amber} label="Cartons jaunes" icon={<CardRect color={C.amber} />} />
          <Pill value={stats.redCards}    color={C.rose}  label="Cartons rouges" icon={<CardRect color={C.rose}  />} />
        </>
      )}

      {view === 'penalties' && (
        <>
          <Pill value={stats.penaltiesScored} color={C.emerald} label="Pénaltys marqués" icon={<PenIcon sx={{ fontSize: 11, color: C.emerald }} />} />
          <Pill value={stats.penaltiesMissed} color={C.rose}    label="Pénaltys manqués" icon={<PenIcon sx={{ fontSize: 11, color: C.rose    }} />} />
        </>
      )}
    </Box>
  );
};

/* ─── Summary chips in header ────────────────────────────────────────────── */
const SummaryChips = ({ ranked, view }) => {
  const chip = (label, color) => (
    <Chip key={label} label={label} size="small" sx={{
      background: `${color}15`, border: `1px solid ${color}30`,
      color, fontFamily: 'Syne', fontWeight: 700, fontSize: '0.72rem',
    }} />
  );

  if (view === 'goals') {
    const g = ranked.reduce((s, p) => s + p._s.goals,   0);
    const a = ranked.reduce((s, p) => s + p._s.assists, 0);
    return <Box sx={{ display:'flex', gap:0.8 }}>{chip(`⚽ ${g}`, C.emerald)}{chip(`A ${a}`, C.accent)}</Box>;
  }
  if (view === 'cards') {
    const y = ranked.reduce((s, p) => s + p._s.yellowCards, 0);
    const r = ranked.reduce((s, p) => s + p._s.redCards,    0);
    return <Box sx={{ display:'flex', gap:0.8 }}>{chip(`🟨 ${y}`, C.amber)}{chip(`🟥 ${r}`, C.rose)}</Box>;
  }
  if (view === 'penalties') {
    const sc = ranked.reduce((s, p) => s + p._s.penaltiesScored, 0);
    const ms = ranked.reduce((s, p) => s + p._s.penaltiesMissed, 0);
    return <Box sx={{ display:'flex', gap:0.8 }}>{chip(`✓ ${sc}`, C.emerald)}{chip(`✗ ${ms}`, C.rose)}</Box>;
  }
  return null;
};

/* ─── Column headers per view ────────────────────────────────────────────── */
const Headers = ({ view }) => (
  <Box sx={{ display:'flex', alignItems:'center', gap:1.2, px:1, mb:0.5 }}>
    <Box sx={{ minWidth: 16 }} /><Box sx={{ width: 32 }} />
    <ColH label="Joueur" minWidth={0} align="left" />
    {view === 'goals' && (<><ColH label="Buts" /><ColH label="Passes" /></>)}
    {view === 'cards' && (<><ColH label="🟨" /><ColH label="🟥" /></>)}
    {view === 'penalties' && (<><ColH label="Scorés" /><ColH label="Manqués" /></>)}
  </Box>
);

/* ─── Main ───────────────────────────────────────────────────────────────── */
const PlayerGoalStatsCard = ({ players = [], goalStats = new Map(), delay = 0 }) => {
  const [view,    setView]    = useState('goals');
  const [showAll, setShowAll] = useState(false);

  const EMPTY_STATS = { goals:0, assists:0, yellowCards:0, redCards:0, penaltiesScored:0, penaltiesMissed:0, gamesPlayed:0 };

  const allPlayers = players.map((p) => ({
    ...p,
    _s: goalStats.get(p._id?.toString()) || { ...EMPTY_STATS },
  }));

  const ranked = allPlayers
    .filter((p) => hasActivity(p._s, view))
    .sort((a, b) => sortByView(a._s, b._s, view));

  const PAGE      = 8;
  const displayed = showAll ? ranked : ranked.slice(0, PAGE);

  const legend = [
    { label: 'Gardien',   color: '#A78BFA' },
    { label: 'Défenseur', color: C.emerald  },
    { label: 'Milieu',    color: C.accent   },
    { label: 'Attaquant', color: C.rose     },
  ];

  const emptyMsg = { goals:'Aucun but enregistré.', cards:'Aucun carton enregistré.', penalties:'Aucun pénalty enregistré.' }[view];

  return (
    <GlowCard delay={delay} accent={C.rose}>
      <CardContent sx={{ p: 3 }}>

        {/* Header */}
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1.5, flexWrap:'wrap', gap:1 }}>
          <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
            <TrophyIcon sx={{ color: C.rose, fontSize: 20 }} />
            <Typography sx={{ fontFamily:'Syne', fontWeight:700, color:C.text, fontSize:'1rem' }}>
              Stats joueurs
            </Typography>
          </Box>
          <SummaryChips ranked={ranked} view={view} />
        </Box>

        {/* View toggle */}
        <ToggleButtonGroup
          value={view} exclusive size="small"
          onChange={(_, v) => { if (v) { setView(v); setShowAll(false); } }}
          sx={{ mb:1.8, '& .MuiToggleButtonGroup-grouped': { border:`1px solid ${C.border} !important`, borderRadius:'8px !important', mx:0.3 } }}
        >
          {VIEWS.map(({ value: val, label }) => (
            <ToggleButton key={val} value={val} sx={{
              fontFamily:'DM Sans', fontSize:'0.72rem', color:C.muted,
              textTransform:'none', px:1.5, py:0.5,
              '&.Mui-selected': { background:`${C.accent}20 !important`, color:`${C.accent} !important`, borderColor:`${C.accent}50 !important` },
            }}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Position legend */}
        <Box sx={{ display:'flex', gap:1.2, flexWrap:'wrap', mb:1.5 }}>
          {legend.map(({ label, color }) => (
            <Box key={label} sx={{ display:'flex', alignItems:'center', gap:0.4 }}>
              <Box sx={{ width:7, height:7, borderRadius:'50%', background:color }} />
              <Typography sx={{ fontFamily:'DM Sans', color:C.muted, fontSize:'0.67rem' }}>{label}</Typography>
            </Box>
          ))}
        </Box>

        {/* Column headers */}
        <Headers view={view} />
        <Divider sx={{ borderColor: C.border, mb: 0.5 }} />

        {/* Rows */}
        {ranked.length === 0 ? (
          <Typography sx={{ fontFamily:'DM Sans', color:C.muted, fontSize:'0.82rem', py:2.5, textAlign:'center' }}>
            {emptyMsg}
          </Typography>
        ) : (
          <>
            <Box sx={{
              maxHeight: showAll ? 'none' : 400, overflowY: showAll ? 'visible' : 'auto', pr: 0.5,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': { background: C.border, borderRadius: 99 },
            }}>
              {displayed.map((player, i) => (
                <PlayerRow key={player._id} player={player} stats={player._s} rank={i+1} index={i} view={view} />
              ))}
            </Box>
            {ranked.length > PAGE && (
              <Box onClick={() => setShowAll(v => !v)} sx={{
                mt:1.5, textAlign:'center', cursor:'pointer',
                color:C.accent, fontFamily:'DM Sans', fontSize:'0.75rem',
                opacity:0.75, '&:hover': { opacity:1 },
              }}>
                {showAll ? '▲ Réduire' : `▼ Voir les ${ranked.length - PAGE} autres`}
              </Box>
            )}
          </>
        )}

      </CardContent>
    </GlowCard>
  );
};

export default PlayerGoalStatsCard;